import {
  Box,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text, useMediaQuery,
} from "@chakra-ui/react";
import React from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { FaEraser } from "react-icons/fa";
import { FormattedMessage, useIntl } from "react-intl";
import { getRandomIndex } from "../utils";
import APButton from "./APButton";
import {OperatorModel} from "../types";

interface AssistanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  operator: OperatorModel;
}

export default function AssistanceModal(props: AssistanceModalProps) {
  const { isOpen, onClose, operator } = props;
  const intl = useIntl();
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      closeOnOverlayClick={true}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay>
        <ModalContent sx={{maxWidth: isLargeScreen ? "40rem" : "32rem"}}>
          <ModalBody className="ap-dialog-body">
            <Box
              sx={{
                background: "#F5F3EF",
                borderRadius: "20px 20px 0px 0px",
                textAlign: "center",
                padding: isLargeScreen ? "20px 30px" : "16px 24px",
                color: "#000",
              }}
            >
              <Box
                sx={{
                  lineHeight: isLargeScreen ? "1.5" : "1.3",
                  fontSize: isLargeScreen ? "22px" : "18px",
                  fontWeight: "bold",
                }}
              >
                <FormattedMessage id={`helper-label-${operator}`} defaultMessage={"Aide"}/>
              </Box>
            </Box>
            <Box sx={{ padding: isLargeScreen ? "10px 30px" : "8px 24px" }}>
              <Box>
                <Text lineHeight={isLargeScreen ? "1.5" : "1.3"} fontSize={isLargeScreen ? "20px" : "16px"} fontWeight="bold">
                  <FormattedMessage id={`do-the-operation-${operator}`} defaultMessage={"Faire l’opération"}/>
                </Text>
                <Text
                  lineHeight={isLargeScreen ? "1.5" : "1.3"}
                  fontSize={isLargeScreen ? "20px" : "16px"}
                  sx={{ mt: isLargeScreen ? "10px" : "8px" }}
                >
                  <FormattedMessage
                    id={`drag-the-numbers-${operator}`}
                    defaultMessage={"Glisse les chiffres dans la zone de calcul de ton opération."}
                  />
                </Text>
                <Box sx={{ mt: isLargeScreen ? "10px" : "8px" }}>
                  <Text lineHeight={isLargeScreen ? "1.5" : "1.3"} fontSize={isLargeScreen ? "20px" : "16px"} as="span">
                    {intl.formatMessage({
                      id: `delete-number-instrustion-${operator}`,
                      defaultMessage: "Pour effacer une chiffre de la zone de calcul: glisse la dehors ou glisse l’effaceur {eraser} sur elle."
                    })
                      .split("{eraser}")
                      .map((text, index) => (
                        <React.Fragment key={index}>
                          {text}
                          {index > 0 ? null : (
                            <Box
                              as="span"
                              sx={{
                                width: isLargeScreen ? "30px" : "24px",
                                height: isLargeScreen ? "30px" : "24px",
                                border: "1px solid #E7E1D8",
                                padding: isLargeScreen ? "5px" : "4px",
                                borderRadius: "2px",
                                mx: isLargeScreen ? "5px" : "4px",
                                "& svg": {
                                  display: "inline-block",
                                },
                              }}
                            >
                              <FaEraser />
                            </Box>
                          )}
                        </React.Fragment>
                      ))
                    }
                  </Text>
                </Box>
              </Box>
              <Box display="flex" columnGap={isLargeScreen ? "20px" : "16px"}>
                <Box>
                  <Text
                    lineHeight={isLargeScreen ? "1.5" : "1.3"}
                    fontSize={isLargeScreen ? "20px" : "16px"}
                    fontWeight="bold"
                    sx={{ mt: isLargeScreen ? "30px" : "24px" }}
                  >
                    <FormattedMessage
                      id={`instructions-loan-heading-${operator}`}
                      defaultMessage={"Faire des emprunts"}
                    />
                  </Text>
                  <Box sx={{ mt: isLargeScreen ? "10px" : "8px" }}>
                    <Text lineHeight={isLargeScreen ? "1.5" : "1.3"} fontSize={isLargeScreen ? "20px" : "16px"} as="span">
                      {intl.formatMessage({
                        id: `instructions-loan-description-${operator}`,
                        defaultMessage: "Si tu as besoin des emprunts, clique sur le bouton {bold}Emprunt{bold}, puis selectionne le chiffre auquel tu souhaite enlever une dizaine et ensuite celui auquel tu veux ajouter 10 unités."
                      }).split("{bold}").map((text, index) => (
                        <React.Fragment key={index}>
                          <Text
                            lineHeight={isLargeScreen ? "1.5" : "1.3"}
                            fontSize={isLargeScreen ? "20px" : "16px"}
                            fontWeight={index % 2 === 0 ? "regular" : "bold"}
                            as="span"
                          >
                            {text}
                          </Text>
                        </React.Fragment>
                      ))}
                    </Text>
                  </Box>
                </Box>
                <Image
                  src={intl.formatMessage({
                    id: `file-tutorial-${operator}`, defaultMessage: `${process.env.REACT_APP_BASE_URL}assistance.gif`
                  })}
                  width={isLargeScreen ? "252px" : "200px"}
                  height={isLargeScreen ? "252px" : "200px"}
                />
              </Box>
              <Box sx={{ mt: isLargeScreen ? "10px" : "8px" }}>
                <Text
                  lineHeight={isLargeScreen ? "1.5" : "1.3"}
                  fontSize={isLargeScreen ? "20px" : "16px"}
                  fontWeight="bold"
                >
                  <FormattedMessage
                    id={`instructions-loan-need-help-heading-${operator}`}
                    defaultMessage={"Besoin d’aide?"}
                  />
                </Text>
                <Box sx={{ mt: isLargeScreen ? "10px" : "8px" }}>
                  {intl.formatMessage({
                    id: `instructions-loan-need-help-description-${operator}`,
                    defaultMessage: "À tout moment, clique sur{info}pour t’aider dans la résolution de l’opération!"
                  })
                    .split("{info}")
                    .map((text, index) => (
                      <React.Fragment key={index}>
                        <Text lineHeight={isLargeScreen ? "1.5" : "1.3"} fontSize={isLargeScreen ? "20px" : "16px"} as="span">
                          {text}
                        </Text>
                        {index > 0 ? null : (
                          <Box
                            as="span"
                            sx={{
                              width: isLargeScreen ? "30px" : "24px",
                              height: isLargeScreen ? "30px" : "24px",
                              mx: isLargeScreen ? "5px" : "4px",
                              "& svg": {
                                display: "inline-block",
                              },
                            }}
                          >
                            <AiOutlineInfoCircle size={isLargeScreen ? "20px" : "16px"} />
                          </Box>
                        )}
                      </React.Fragment>
                    ))
                  }
                </Box>
              </Box>
            </Box>
          </ModalBody>

          <ModalFooter
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              background: "#F5F3EF",
              borderRadius: "0px 0px 20px 20px",
            }}
          >
            <APButton
              sx={{
                width: isLargeScreen ? "378px" : "300px",
                fontSize: isLargeScreen ? "1.26rem" : "1rem",
                height: isLargeScreen ? "50px" : "40px"
              }}
              colorScheme="primary"
              onClick={handleClose}
            >
              <FormattedMessage
                id={`its-understood-${operator}`}
                defaultMessage={"C’est compris"}
              />
            </APButton>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
}
